import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { APP_VERSION } from '../../version';
import {
  Home,
  Wrench,
  BarChart3,
  Briefcase,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  Radio,
  Archive,
  Activity,
  Palette,
  Bell,
  Map,
  Globe,
  HeartPulse,
  ClipboardCheck,
  RefreshCcw,
  Building2,
  Landmark,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  {
    name: 'Notifications',
    href: '/notifications',
    icon: Bell,
  },
  {
    name: 'Workspaces',
    href: '/workspaces',
    icon: Briefcase,
    children: [
      { name: 'My Workspaces', href: '/workspaces', icon: Briefcase },
      { name: 'Topics / Dept.', href: '/workspaces/topics', icon: Palette },
    ],
  },
  {
    name: 'Tools',
    href: '/tools',
    icon: Wrench,
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    roles: ['SYSTEM_ADMIN'],
  },
  {
    name: 'Coverage',
    href: '/coverage',
    icon: Map,
    roles: ['SYSTEM_ADMIN', 'ORG_OWNER'],
    children: [
      {
        name: 'Jurisdictions',
        href: '/coverage/jurisdiction',
        icon: Globe,
      },
    ],
  },
  {
    name: 'Data Sources',
    href: '/sources',
    icon: Radio,
    roles: ['SYSTEM_ADMIN'],
    children: [
      {
        name: 'Pipeline Reconcile',
        href: '/sources/reconcile',
        icon: RefreshCcw,
        roles: ['SYSTEM_ADMIN'],
      },
      {
        name: 'Artifacts Review',
        href: '/needs-review',
        icon: ClipboardCheck,
        roles: ['SYSTEM_ADMIN'],
      },
      {
        name: 'Health',
        href: '/sources/health',
        icon: HeartPulse,
        roles: ['SYSTEM_ADMIN'],
      },
      {
        name: 'Civic Scraper',
        href: '/sources/civic-scraper',
        icon: Landmark,
        roles: ['SYSTEM_ADMIN'],
      },
      {
        name: 'Coverage Audit',
        href: '/sources/coverage-audit',
        icon: ClipboardCheck,
        roles: ['SYSTEM_ADMIN'],
      },
      { name: 'Artifacts DB', href: '/artifacts', icon: Archive },
    ],
  },
  { name: 'Settings', href: '/settings', icon: Settings },
  {
    name: 'Admin',
    href: '/admin',
    icon: Shield,
    roles: ['SYSTEM_ADMIN'],
    children: [
      { name: 'Organizations', href: '/admin/organizations', icon: Building2 },
      {
        name: 'Entitlements',
        href: '/admin/entitlements',
        icon: ClipboardCheck,
        roles: ['SYSTEM_ADMIN'],
      },
      { name: 'Tool Registry', href: '/admin/tools', icon: Wrench },
      {
        name: 'Source Registration',
        href: '/admin/source-registration',
        icon: Globe,
        roles: ['SYSTEM_ADMIN'],
      },
      { name: 'Engine Connections', href: '/admin/engines', icon: Radio },
      { name: 'Skills', href: '/admin/skills', icon: Palette },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
      {
        name: 'Feedback',
        href: '/admin/feedback',
        icon: Activity,
        roles: ['SYSTEM_ADMIN'],
      },
      {
        name: 'Log Monitor',
        href: '/admin/logs',
        icon: Activity,
      },
      { name: 'System', href: '/admin/system', icon: Settings },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user } = useUser();

  const isVisible = (item: NavItem) =>
    !item.roles || (user && item.roles.includes(user.role));

  const filteredNavigation = navigation.filter(isVisible);

  const renderItem = (item: NavItem, isChild = false) => {
    const Icon = item.icon;
    const visibleChildren = item.children?.filter(isVisible) ?? [];

    const [itemPath] = item.href.split('?');

    // A child is "fully current" when its path matches exactly
    const anyChildCurrent = visibleChildren.some((c) => {
      const [cPath] = c.href.split('?');
      return location.pathname === cPath;
    });

    // Parent path-matching rules (children always use exact match)
    const pathMatches = isChild
      ? location.pathname === itemPath
      : item.href === '/tools'
        ? location.pathname.startsWith('/tools')
        : item.href === '/sources'
          ? location.pathname.startsWith('/sources')
          : item.href === '/admin'
            ? location.pathname.startsWith('/admin')
            : item.href === '/workspaces'
              ? location.pathname === '/workspaces' ||
                location.pathname.startsWith('/workspaces/')
              : location.pathname === itemPath;

    // Parent is highlighted when its own path is active and no child steals it
    const isCurrent = !anyChildCurrent && pathMatches;

    // Parent gets a subtle indicator when a child is active instead
    const isParentActive = !isChild && anyChildCurrent && pathMatches;

    return (
      <div key={item.name}>
        <Link
          to={item.href}
          className={`
            flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
            ${isChild ? 'ml-4 text-sm' : ''}
            ${
              isCurrent
                ? 'bg-blue-600 text-white'
                : isParentActive
                  ? 'text-white'
                  : 'text-gray-100 hover:bg-gray-800 hover:text-white'
            }
            ${!isOpen ? 'justify-center' : ''}
          `}
          title={!isOpen ? item.name : undefined}
        >
          <Icon
            className={`flex-shrink-0 ${isChild ? 'w-4 h-4' : 'w-5 h-5'}`}
          />
          {isOpen && <span className="font-medium">{item.name}</span>}
        </Link>

        {/* Sub-items — only shown when sidebar is open */}
        {isOpen && visibleChildren.length > 0 && (
          <div className="mt-0.5 space-y-0.5">
            {visibleChildren.map((child) => renderItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`
        bg-gray-900 text-white transition-all duration-300 ease-in-out
        ${isOpen ? 'w-64' : 'w-16'}
        relative flex h-screen flex-col overflow-hidden
      `}
    >
      {/* Logo Section */}
      <div className="flex h-16 flex-shrink-0 items-center justify-between px-4 border-b border-gray-800">
        {isOpen && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold">
              G
            </div>
            <span className="font-bold text-lg">MeetingCite</span>
          </div>
        )}
        {!isOpen && (
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold mx-auto">
            G
          </div>
        )}
      </div>

      {/* Navigation — scrolls internally so the dark background always
          reaches the bottom of the viewport, even when the nav list
          (e.g. Admin's sub-items) is taller than the available space. */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-2 py-4 space-y-1">
        {filteredNavigation.map((item) => renderItem(item))}
      </nav>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 bg-gray-900 border border-gray-700 rounded-full p-1 hover:bg-gray-800 transition-colors"
        aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {isOpen ? (
          <ChevronLeft className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>

      {/* Version Info */}
      {isOpen && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-800">
          <div className="text-xs text-gray-400">Version {APP_VERSION}</div>
        </div>
      )}
    </aside>
  );
};
